import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import {
  Autocomplete,
  InputSize,
  InputText,
  Select,
  SelectSize,
  SelectType,
  Textarea,
  IconAttach,
  IconTrash,
  IconLoader,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import StepPartBlock from '../../corporate/parts/step-part-block';
import InfoRow from '../../info-row';
import { GroupInformationTypes } from './types';
import {
  NewApplicationStatus,
  UploadResponse,
} from '../../../../hoc/customers/types';
import { ApplicationDocument } from '../../../../hoc/new-application/types';
import { deleteIsSuccess } from '../../../../hoc/new-application/individual/store/slices';
import { deleteIsSuccess as cofDeleteIsSuccess } from '../../../../hoc/new-application/main-representative/store/slices';
import {
  useAddIndividualApplicationDocumentsMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
  useAddCofApplicationDocumentsMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
} from '../../../../hoc/new-application/store/services';
import { useCustomersUploadMutation } from '../../../../store/documents/services';
import { useCheckDocumentStringNameMutation } from '../../../../../../../store/service';

import cn from '../../new-application.module.css';

const GroupInformation = ({
  questionnarieControl,
  questionnarieClearErrors,
  questionnarieErrors,
  status,
  isEditable,
  countries,
  sourceOfWealth,
  baseCurrency,
  questionnarieSetValue,
  questionnarieFields,
  groupStructurChartFile,
  isCof,
}: GroupInformationTypes) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { applicationId } = useParams();

  const { documents } = useAppSelector(
    (state) => state.newApplications.individualInfo,
  );
  const dispatch = useAppDispatch();

  const [deleteIndividualAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteIndividualApplicationDocumentsMutation();
  const [deleteCofAppicationDocuments, { isLoading: cofDeleteInProgress }] =
    useBatchDeleteCOFApplicationDocumentsMutation();

  const [
    addIndividualApplicationDocument,
    { isLoading: documentSubmitInProgress },
  ] = useAddIndividualApplicationDocumentsMutation();
  const [
    addCofApplicationDocument,
    { isLoading: cofDocumentSubmitInProgress },
  ] = useAddCofApplicationDocumentsMutation();

  const [upload, { isLoading: fileUploadInProgress }] =
    useCustomersUploadMutation();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [uploadedFileError, setUploadedFilesError] = useState<string>('');

  const onUpload = async (files: FileList) => {
    setUploadedFilesError('');

    const file = files[0];

    const checkNameResponse = (await checkDocumentName({
      value: file.name,
    })) as {
      error?: {
        data: {
          message?: {
            constraints: Record<string, string>;
          }[];
        };
      };
    };

    let nextFile;

    if (!checkNameResponse?.error) {
      const response = (await upload(file)) as unknown as {
        data?: UploadResponse[];
        error?: {
          originalStatus: number;
          data?: {
            message?: string;
          };
        };
        message?: string;
      };

      if (response && response.data) {
        nextFile = { ...response.data[0] };

        let fileName = file.name;

        if (response.data[0] && response.data[0]?.mimeType?.includes('image')) {
          fileName = `${file?.name.slice(0, file?.name.lastIndexOf('.'))}.jpg`;
        }

        nextFile.name = fileName;

        const body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
        } = {
          uploadId: nextFile.id,
          name: nextFile.name,
          documentCategoryId: '72b810fc-d635-4937-8a14-f9b77eaa699f',
        };

        let documentResponse;

        if (isCof) {
          documentResponse = (await addCofApplicationDocument({
            id: applicationId as string,
            body,
          })) as {
            error?: {
              data: {
                message?: {
                  constraints: Record<string, string>;
                }[];
              };
            };
          };
        } else {
          documentResponse = (await addIndividualApplicationDocument({
            id: applicationId as string,
            body,
          })) as {
            error?: {
              data: {
                message?: {
                  constraints: Record<string, string>;
                }[];
              };
            };
          };
        }

        if (
          documentResponse.error &&
          documentResponse?.error?.data?.message?.length
        ) {
          const errorItem =
            documentResponse?.error?.data?.message[0].constraints;

          if (errorItem) {
            const [documentValue] = Object.values(errorItem);

            setUploadedFilesError(documentValue);
          }
        }
      } else if (response.error) {
        if (response.error.originalStatus === 413) {
          setUploadedFilesError(`${file.name} size too large`);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (response?.error?.data?.message) {
            setUploadedFilesError(response?.error?.data?.message);
          }
        }
      }
    } else if (
      checkNameResponse?.error &&
      checkNameResponse?.error?.data?.message
    ) {
      const errorItem = checkNameResponse?.error?.data?.message[0]?.constraints;

      if (errorItem) {
        setUploadedFilesError(`${file.name} ${Object.values(errorItem)[0]}`);
      }
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const onDeleteDocuments = async () => {
    let response;

    if (isCof) {
      response = await deleteCofAppicationDocuments({
        id: applicationId as string,
        documentId: [groupStructurChartFile?.id as string],
      });
    } else {
      response = await deleteIndividualAppicationDocuments({
        id: applicationId as string,
        documentId: [groupStructurChartFile?.id as string],
      });
    }

    if (response) {
      const nextDocuments = documents?.filter(
        (document) => document?.id !== groupStructurChartFile?.id,
      );

      if (isCof) {
        dispatch(cofDeleteIsSuccess(nextDocuments as ApplicationDocument[]));
      } else {
        dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
      }
    }
  };

  return (
    <StepPartBlock title="Information about group" zIndex={26}>
      <InfoRow
        title="Are you an owner of any group of companies?"
        id="belongsToGroup"
        style={{
          position: 'relative',
          zIndex: 17,
        }}
        lessPadding
      >
        <Controller
          control={questionnarieControl}
          name="belongsToGroup"
          defaultValue=""
          render={({ field: { onChange, value, name } }) => (
            <Select
              name={name}
              placeholder="Select an option..."
              list={[
                {
                  name: 'Yes',
                  value: 'yes',
                },
                {
                  name: 'No',
                  value: 'no',
                },
              ]}
              value={value as string}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange(e);
                questionnarieClearErrors(['belongsToGroup']);

                if (e.target.value === 'no') {
                  questionnarieClearErrors([
                    'parentCompanyName',
                    'parentCompanyCountryId',
                    'subsidiariesInfo',
                    'mainActivities',
                    'groupWealthSourceIds',
                    'estimatedTotalWealthEuro',
                  ]);

                  questionnarieSetValue('parentCompanyName', '');
                  questionnarieSetValue('parentCompanyCountryId', '');
                  questionnarieSetValue('subsidiariesInfo', '');
                  questionnarieSetValue('mainActivities', '');
                  questionnarieSetValue('groupWealthSourceIds', []);
                  questionnarieSetValue('estimatedTotalWealthEuro', '');
                }
              }}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              error={questionnarieErrors?.belongsToGroup?.message}
              fullWidth
              borderless
            />
          )}
        />
      </InfoRow>
      {questionnarieFields?.belongsToGroup === 'yes' && (
        <>
          <InfoRow
            title="Group Structure Chart"
            id="groupStructurChartFile"

            lessPadding
          >
            <>
              <label
                htmlFor="groupStructurChartFile"
                aria-label="Group Structur Chart"
                className={cn.groupStructurChartFileInputWrapper}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  id="groupStructurChartFile"
                  className={cn.groupStructurChartFileInput}
                  onChange={(e) => onUpload(e.target.files as FileList)}
                />
                {!groupStructurChartFile && (
                  <>
                    {!documentSubmitInProgress &&
                      !fileUploadInProgress &&
                      !checkDocumentNameInProgress &&
                      !cofDocumentSubmitInProgress && (
                        <button
                          type="button"
                          className={cn.groupStructurChartFileButton}
                          onClick={() => fileInputRef?.current?.click()}
                        >
                          <IconAttach width={16} height={16} />
                          Add file
                        </button>
                      )}
                    {(documentSubmitInProgress ||
                      cofDocumentSubmitInProgress ||
                      fileUploadInProgress ||
                      checkDocumentNameInProgress) && (
                      <div className={cn.documentUploadInProgress}>
                        Document uploading...
                        <IconLoader width={16} height={16} />
                      </div>
                    )}
                  </>
                )}
                {groupStructurChartFile && (
                  <div className={cn.groupStructurChartFileContent}>
                    <div className={cn.groupStructurChartFileNameWrapper}>
                      <div className={cn.groupStructurChartFileName}>
                        {groupStructurChartFile?.name}
                      </div>
                    </div>
                    {status !== NewApplicationStatus.ACCEPTED &&
                      status !== NewApplicationStatus.DECLINED && (
                        <button
                          type="button"
                          className={cn.groupStructurChartFileDeleteButton}
                          disabled={deleteInProgress || cofDeleteInProgress}
                          onClick={onDeleteDocuments}
                        >
                          {deleteInProgress || cofDeleteInProgress ? (
                            <IconLoader width={16} height={16} />
                          ) : (
                            <IconTrash width={16} height={16} />
                          )}
                        </button>
                      )}
                  </div>
                )}
              </label>
              {uploadedFileError && (
                <div className={cn.uploadGroupStructureError}>
                  {uploadedFileError}
                </div>
              )}
            </>
          </InfoRow>
          {!isCof && (
            <>
              <InfoRow
                title="Name of the parent / managing company"
                id="parentCompanyName"

                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="parentCompanyName"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      placeholder="Enter name..."
                      value={value}
                      size={InputSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['parentCompanyName']);
                      }}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.parentCompanyName?.message}
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Country of incorporation of the parent company"
                id="parentCompanyCountryId"
                style={{
                  position: 'relative',
                  zIndex: 16,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="parentCompanyCountryId"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Autocomplete
                      name={name}
                      placeholder="Select country..."
                      list={countries}
                      value={value}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['parentCompanyCountryId']);
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={
                        questionnarieErrors?.parentCompanyCountryId?.message
                      }
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Subsidiary & associate companies + their incorporation countries"
                id="subsidiariesInfo"
                className={cn.noFlex}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="subsidiariesInfo"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Textarea
                      name={name}
                      title=""
                      placeholder="Enter description..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['subsidiariesInfo']);
                      }}
                      className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                      error={questionnarieErrors?.subsidiariesInfo?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Main business activities of the group"
                id="mainActivities"
                className={cn.noFlex}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="mainActivities"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <Textarea
                      name={name}
                      title=""
                      placeholder="Enter description..."
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['mainActivities']);
                      }}
                      className={`borderless ${cn.stepOneTextarea} ${cn.pepTextarea}`}
                      error={questionnarieErrors?.mainActivities?.message}
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                    />
                  )}
                />
              </InfoRow>
              <InfoRow
                title="Source of wealth of the group (origin of total assets)"
                id="groupWealthSourceIds"
                style={{
                  position: 'relative',
                  zIndex: 15,
                }}
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="groupWealthSourceIds"
                  defaultValue={[]}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      name={name}
                      placeholder="Select an options..."
                      list={
                        sourceOfWealth?.map((item) => ({
                          name: item.value,
                          value: item.id,
                        })) || []
                      }
                      value={value}
                      size={SelectSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['groupWealthSourceIds']);

                        if (
                          !(e.target.value as string[])?.includes('Other') &&
                          questionnarieFields?.groupWealthSourceIds
                        ) {
                          questionnarieClearErrors(['groupWealthSourceOther']);
                          questionnarieSetValue('groupWealthSourceOther', '');
                        }
                      }}
                      selectWrapperClassName={
                        status === NewApplicationStatus.ACCEPTED ||
                        status === NewApplicationStatus.DECLINED
                          ? cn.selectDisabled
                          : ''
                      }
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={questionnarieErrors?.groupWealthSourceIds?.message}
                      mode={SelectType.MULTIPLE}
                      fullWidth
                      borderless
                    />
                  )}
                />
              </InfoRow>
              {questionnarieFields?.groupWealthSourceIds?.find((item) =>
                sourceOfWealth
                  ?.find((client) => item === client.id)
                  ?.value?.includes('Other'),
              ) && (
                <InfoRow
                  title="Provide details"
                  id="groupWealthSourceOther"
                  className={cn.noFlex}
                  lessPadding
                >
                  <Controller
                    control={questionnarieControl}
                    name="groupWealthSourceOther"
                    defaultValue=""
                    render={({ field: { onChange, value, name } }) => (
                      <Textarea
                        name={name}
                        title=""
                        placeholder="Enter details..."
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          questionnarieClearErrors(['groupWealthSourceOther']);
                        }}
                        className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
                        error={
                          questionnarieErrors?.groupWealthSourceOther?.message
                        }
                        tabIndex={
                          !isEditable &&
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                            ? -1
                            : 0
                        }
                        disabled={
                          !isEditable ||
                          Boolean(
                            status === NewApplicationStatus.ACCEPTED ||
                            status === NewApplicationStatus.DECLINED,
                          )
                        }
                      />
                    )}
                  />
                </InfoRow>
              )}
              <InfoRow
                title={`Estimated total wealth of the group (in ${baseCurrency?.name})`}
                id="estimatedTotalWealthEuro"
                lessPadding
              >
                <Controller
                  control={questionnarieControl}
                  name="estimatedTotalWealthEuro"
                  defaultValue=""
                  render={({ field: { onChange, value, name } }) => (
                    <InputText
                      name={name}
                      placeholder="Enter amount..."
                      value={value}
                      size={InputSize.SMALL}
                      onChange={(e) => {
                        onChange(e);
                        questionnarieClearErrors(['estimatedTotalWealthEuro']);
                      }}
                      className="borderless"
                      disabled={
                        !isEditable ||
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                      }
                      tabIndex={
                        !isEditable &&
                        Boolean(
                          status === NewApplicationStatus.ACCEPTED ||
                          status === NewApplicationStatus.DECLINED,
                        )
                          ? -1
                          : 0
                      }
                      error={
                        questionnarieErrors?.estimatedTotalWealthEuro?.message
                      }
                      currency={baseCurrency}
                      amountInput
                    />
                  )}
                />
              </InfoRow>
            </>
          )}
        </>
      )}
    </StepPartBlock>
  );
};

export default GroupInformation;
